import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modern/components/ui/dialog";
import { Button } from "@/modern/components/ui/button";
import { ChevronDown, Loader2 } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "motion/react";
import type { TemporalPhaseTag } from "@/react/components/marketing/temporal";
import { cn } from "@/modern/utils";
import type { PromoteSurfaceAsset, SurfaceSolutionTag } from "../api/types";
import { getAcceptReadiness, initialAcceptFieldsFromAssets } from "../utils/mergeTagIds";
import PromoteSurfaceAcceptFields, { type ContentRoleTag } from "./PromoteSurfaceAcceptFields";

/** How many selection thumbnails to show before a “+N” overflow chip. */
const THUMB_PREVIEW_LIMIT = 6;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: SurfaceSolutionTag[];
  temporalTags: TemporalPhaseTag[];
  contentRoleTags?: ContentRoleTag[];
  /** Selected assets — used to prefill AI / existing tag_ids. */
  selectedAssets: PromoteSurfaceAsset[];
  busy?: boolean;
  boardUserAvailable?: boolean;
  onConfirm: (payload: {
    assets: PromoteSurfaceAsset[];
    solutionTagIds: number[];
    temporalTagId: number | null;
    contentRoleIds: number[];
  }) => void | Promise<void>;
};

export default function PromoteSurfaceBulkAcceptSheet({
  open,
  onOpenChange,
  tags,
  temporalTags,
  contentRoleTags = [],
  selectedAssets,
  busy,
  boardUserAvailable = true,
  onConfirm,
}: Props) {
  const reduceMotion = useReducedMotion();
  const [selected, setSelected] = useState<number[]>([]);
  const [suggested, setSuggested] = useState<number[]>([]);
  const [temporalTagId, setTemporalTagId] = useState<number | null>(null);
  const [contentRoleIds, setContentRoleIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [bodyScrollEl, setBodyScrollEl] = useState<HTMLDivElement | null>(null);
  const [moreBelow, setMoreBelow] = useState(false);
  /** Freeze the accept set at open so feed/tag polling cannot reset the form mid-edit. */
  const [targets, setTargets] = useState<PromoteSurfaceAsset[]>([]);
  const wasOpenRef = useRef(false);
  const openSnapshotRef = useRef({
    selectedAssets,
    tags,
    temporalTags,
    contentRoleTags,
  });
  openSnapshotRef.current = {
    selectedAssets,
    tags,
    temporalTags,
    contentRoleTags,
  };

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      setTargets([]);
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;

    const snap = openSnapshotRef.current;
    setSubmitting(false);
    const snapshot = snap.selectedAssets.slice();
    setTargets(snapshot);
    const initial = initialAcceptFieldsFromAssets(
      snapshot,
      snap.tags,
      snap.temporalTags.map((t) => t.id),
      snap.contentRoleTags.map((t) => t.id)
    );
    setSelected(initial.solutionTagIds);
    setSuggested(initial.solutionTagIds);
    setTemporalTagId(initial.temporalTagId);
    setContentRoleIds(initial.contentRoleIds);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    bodyScrollEl?.scrollTo({ top: 0 });
  }, [open, bodyScrollEl]);

  useLayoutEffect(() => {
    if (!open || !bodyScrollEl) {
      setMoreBelow(false);
      return;
    }
    const el = bodyScrollEl;
    const sync = () =>
      setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 2);
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", sync);
      ro.disconnect();
    };
  }, [open, bodyScrollEl, selected.length, temporalTagId, contentRoleIds.length, tags.length]);

  const { canAccept, needsSolutionTag, needsTemporalTag } = getAcceptReadiness({
    solutionTagIds: selected,
    temporalTagId,
    temporalTagCount: temporalTags.length,
  });

  const previewAssets = targets.length ? targets : selectedAssets;
  const previewThumbs = useMemo(
    () => previewAssets.slice(0, THUMB_PREVIEW_LIMIT),
    [previewAssets]
  );
  const previewCount = previewAssets.length;
  const overflowCount = Math.max(0, previewCount - previewThumbs.length);
  const formBusy = Boolean(busy) || submitting;
  const confirmDisabled =
    formBusy || !canAccept || !boardUserAvailable || previewCount < 1;

  const disableHints: string[] = [];
  if (!formBusy) {
    if (!boardUserAvailable) {
      disableHints.push("A board user is required before you can accept.");
    } else if (previewCount < 1) {
      disableHints.push("Select at least one asset to accept.");
    } else if (needsTemporalTag) {
      disableHints.push("Select a temporal phase to enable Accept.");
    } else if (needsSolutionTag) {
      disableHints.push("Select at least one solution tag to enable Accept.");
    }
  }

  const title =
    previewCount === 1 ? "Accept 1 asset" : `Accept ${previewCount} assets`;
  const applySummary =
    previewCount === 1
      ? "These tags apply to the selected asset."
      : `These tags apply to all ${previewCount} selected assets.`;

  const handleConfirm = async () => {
    if (confirmDisabled) return;
    setSubmitting(true);
    try {
      await onConfirm({
        assets: previewAssets,
        solutionTagIds: selected,
        temporalTagId,
        contentRoleIds,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        if (!next) {
          setSelected([]);
          setSuggested([]);
          setTemporalTagId(null);
          setContentRoleIds([]);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="!flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="shrink-0 space-y-3 pr-12">
          <DialogTitle>{title}</DialogTitle>
          {previewCount > 0 && (
            <div
              className="flex flex-wrap items-center gap-2"
              aria-label={`${previewCount} selected images`}
            >
              {previewThumbs.map((asset) => {
                const src = asset.thumbnail_url || asset.url || "";
                return (
                  <div
                    key={asset.id}
                    className="relative h-14 w-14 overflow-hidden rounded-md border border-border bg-muted shadow-sm"
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={asset.caption || asset.alt_text || `Asset ${asset.id}`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">
                        No preview
                      </div>
                    )}
                  </div>
                );
              })}
              {overflowCount > 0 && (
                <div
                  className={cn(
                    "flex h-14 min-w-14 items-center justify-center rounded-md border border-dashed border-border",
                    "bg-muted/60 px-2 text-xs font-semibold tabular-nums text-muted-foreground"
                  )}
                  aria-label={`${overflowCount} more selected`}
                >
                  +{overflowCount}
                </div>
              )}
            </div>
          )}
          <DialogDescription className="text-sm text-muted-foreground">
            {applySummary}
          </DialogDescription>
        </DialogHeader>

        <div className="relative min-h-0 shrink overflow-hidden">
          <div
            ref={setBodyScrollEl}
            className="max-h-[calc(85vh-14rem)] space-y-4 overflow-y-auto overscroll-contain px-swivu-base pb-9 pt-4 [scrollbar-width:thin]"
          >
            <PromoteSurfaceAcceptFields
              tags={tags}
              temporalTags={temporalTags}
              contentRoleTags={contentRoleTags}
              solutionTagIds={selected}
              onSolutionTagIdsChange={setSelected}
              suggestedSolutionTagIds={suggested}
              temporalTagId={temporalTagId}
              onTemporalTagIdChange={setTemporalTagId}
              contentRoleIds={contentRoleIds}
              onContentRoleIdsChange={setContentRoleIds}
              disabled={formBusy}
              showInlineHints
            />
          </div>

          {moreBelow && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] flex justify-center bg-gradient-to-t from-background from-30% to-transparent pb-1 pt-10">
              <button
                type="button"
                className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm hover:text-foreground"
                aria-label="More fields below"
                onClick={() =>
                  bodyScrollEl?.scrollBy({
                    top: Math.max(120, Math.floor((bodyScrollEl?.clientHeight ?? 240) * 0.45)),
                    behavior: reduceMotion ? "auto" : "smooth",
                  })
                }
              >
                <ChevronDown className="h-4 w-4" strokeWidth={2.5} aria-hidden />
              </button>
            </div>
          )}
        </div>

        <DialogFooter className="relative z-[2] shrink-0 flex-col items-stretch gap-2 bg-background sm:flex-col sm:space-x-0">
          {disableHints.length > 0 && (
            <div
              className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
              role="status"
            >
              {disableHints.map((hint) => (
                <p key={hint}>{hint}</p>
              ))}
            </div>
          )}
          <div className="flex flex-row justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={formBusy}>
              Cancel
            </Button>
            <Button disabled={confirmDisabled} onClick={() => void handleConfirm()}>
              {formBusy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Accepting…
                </>
              ) : (
                "Accept"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
